import { useState, useEffect } from "react";
import axios from "axios";
import {
  LayoutDashboard,
  ShoppingCart,
  Users,
  Package,
  Tag,
  TrendingUp,
  IndianRupee,
  Clock,
  CheckCircle2,
  XCircle,
  Truck,
  RefreshCw,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";

const API = "/api";

// ── helpers ────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);

const fmtRupee = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// ── status config ─────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  scheduled:        { label: "Scheduled",        color: "#6366f1", bg: "bg-indigo-50", text: "text-indigo-700", icon: Clock },
  pending:          { label: "Pending",          color: "#f59e0b", bg: "bg-amber-50",   text: "text-amber-700",  icon: Clock },
  confirmed:        { label: "Confirmed",        color: "#3b82f6", bg: "bg-blue-50",    text: "text-blue-700",   icon: CheckCircle2 },
  processing:       { label: "Processing",       color: "#8b5cf6", bg: "bg-violet-50",  text: "text-violet-700", icon: RefreshCw },
  out_for_delivery: { label: "Out for Delivery", color: "#6C42F5", bg: "bg-sky-50",     text: "text-sky-700",    icon: Truck },
  delivered:        { label: "Delivered",        color: "#22c55e", bg: "bg-green-50",   text: "text-green-700",  icon: CheckCircle2 },
  cancelled:        { label: "Cancelled",        color: "#ef4444", bg: "bg-red-50",     text: "text-red-700",    icon: XCircle },
};

// ── stat card ─────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, iconBg, iconColor }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex items-center gap-4">
    <div className={`p-3 rounded-xl ${iconBg} flex-shrink-0`}>
      <Icon size={22} className={iconColor} />
    </div>
    <div className="min-w-0">
      <p className="text-sm text-gray-500 truncate">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ── status badge ──────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: "bg-gray-100", text: "text-gray-600" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
};

// ── main component ────────────────────────────────────────────────────────
const DashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("ecomdemo_token");
      const res = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error("Dashboard fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-[#6C42F5]" />
      </div>
    );
  }

  if (!data) return null;

  // Pie data for order statuses
  const pieData = Object.entries(data.status_counts)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({
      name: STATUS_CONFIG[k]?.label || k,
      value: v,
      color: STATUS_CONFIG[k]?.color || "#94a3b8",
    }));

  return (
    <div data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <LayoutDashboard className="text-[#6C42F5]" size={28} />
            Dashboard
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Live snapshot of your business</p>
        </div>
        <button
          onClick={fetchDashboard}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-[#6C42F5] transition-colors"
        >
          <RefreshCw size={15} />
          Refresh
        </button>
      </div>

      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
        <StatCard
          icon={ShoppingCart}
          label="Total Orders"
          value={fmt(data.total_orders)}
          iconBg="bg-[#F1EDFF]"
          iconColor="text-[#6C42F5]"
        />
        <StatCard
          icon={TrendingUp}
          label="Orders This Month"
          value={fmt(data.orders_this_month)}
          iconBg="bg-violet-50"
          iconColor="text-violet-600"
        />
        <StatCard
          icon={IndianRupee}
          label="Total Revenue"
          value={fmtRupee(data.revenue_total)}
          iconBg="bg-green-50"
          iconColor="text-green-600"
        />
        <StatCard
          icon={IndianRupee}
          label="Revenue This Month"
          value={fmtRupee(data.revenue_this_month)}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
        />
        <StatCard
          icon={Users}
          label="Total Users"
          value={fmt(data.total_users)}
          iconBg="bg-orange-50"
          iconColor="text-orange-500"
        />
        <StatCard
          icon={Package}
          label="Products"
          value={fmt(data.total_products)}
          iconBg="bg-pink-50"
          iconColor="text-pink-500"
        />
        <StatCard
          icon={Tag}
          label="Categories"
          value={fmt(data.total_categories)}
          iconBg="bg-amber-50"
          iconColor="text-amber-500"
        />
        <StatCard
          icon={CheckCircle2}
          label="Delivered Orders"
          value={fmt(data.status_counts.delivered || 0)}
          iconBg="bg-green-50"
          iconColor="text-green-600"
        />
        <StatCard
          icon={XCircle}
          label="Cancelled Orders"
          value={fmt(data.cancelled_orders || 0)}
          iconBg="bg-red-50"
          iconColor="text-red-600"
        />
        <StatCard
          icon={IndianRupee}
          label="Cancelled Orders Revenue"
          value={fmtRupee(data.cancelled_revenue || 0)}
          iconBg="bg-rose-50"
          iconColor="text-rose-600"
        />
      </div>

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">

        {/* Bar chart — orders last 7 days */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Orders — Last 7 Days</h2>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.daily_orders} barCategoryGap="35%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="day"
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 }}
                cursor={{ fill: "#eef2fb" }}
              />
              <Bar dataKey="orders" fill="#6C42F5" radius={[6, 6, 0, 0]} name="Orders" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie chart — order status */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4">Orders by Status</h2>
          {pieData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="45%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 13 }}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  formatter={(value) => value}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[220px] text-gray-400 text-sm">
              No order data yet
            </div>
          )}
        </div>
      </div>

      {/* ── Bottom Row ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

        {/* Recent Orders */}
        <div className="xl:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">Recent Orders</h2>
          </div>
          {data.recent_orders.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No orders yet</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {data.recent_orders.map((o) => (
                <div key={o.id} className="px-6 py-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 text-sm truncate">{o.user_name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{o.user_phone} · {fmtDate(o.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="font-semibold text-gray-800 text-sm">{fmtRupee(o.total_amount)}</span>
                    <StatusBadge status={o.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Products + Status Breakdown */}
        <div className="flex flex-col gap-6">

          {/* Top Products */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">Top Products</h2>
            </div>
            {data.top_products.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">No data yet</div>
            ) : (
              <div className="px-6 py-4 space-y-3">
                {data.top_products.map((p, i) => {
                  const maxQty = data.top_products[0]?.quantity || 1;
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-gray-700 truncate max-w-[160px]">{p.name}</span>
                        <span className="text-xs font-semibold text-gray-500">{p.quantity} sold</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#6C42F5] rounded-full"
                          style={{ width: `${(p.quantity / maxQty) * 100}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Status Breakdown */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-800">Status Breakdown</h2>
            </div>
            <div className="px-6 py-4 space-y-2.5">
              {Object.entries(data.status_counts).map(([key, count]) => {
                const cfg = STATUS_CONFIG[key];
                const Icon = cfg.icon;
                return (
                  <div key={key} className={`flex items-center justify-between px-3 py-2 rounded-lg ${cfg.bg}`}>
                    <div className="flex items-center gap-2">
                      <Icon size={14} className={cfg.text} />
                      <span className={`text-xs font-medium ${cfg.text}`}>{cfg.label}</span>
                    </div>
                    <span className={`text-sm font-bold ${cfg.text}`}>{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
